// src/components/AlertOverlay.jsx
import React, { useEffect } from 'react';
import { Button, DangerButton } from '../ui';

export default function AlertOverlay({ alert, onClose }) {
  const isConfirm = alert?.type === 'confirm';

  useEffect(() => {
    if (!alert) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        // For confirm: ESC = cancel
        if (isConfirm && alert.onCancel) alert.onCancel();
        onClose();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [alert, onClose, isConfirm]);

  if (!alert) return null;

  const handleConfirm = async () => {
    try {
      if (alert.onConfirm) {
        await alert.onConfirm();
      }
    } finally {
      onClose();
    }
  };

  const handleCancel = () => {
    if (alert.onCancel) {
      alert.onCancel();
    }
    onClose();
  };

  const borderColor =
    alert.type === 'error'
      ? '#b91c1c'
      : alert.type === 'success'
        ? '#83d466'
        : alert.type === 'warning'
          ? '#d97706'
          : '#0f766e';

  const icon =
    alert.type === 'error'
      ? '⚠️'
      : alert.type === 'success'
        ? '✅'
        : alert.type === 'warning'
          ? '⚠️'
          : alert.type === 'confirm'
            ? '❓'
            : 'ℹ️';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.35)',
        zIndex: 10_000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: 80,
      }}
      onClick={() => {
        // Backdrop click should NOT dismiss confirm dialogs.
        if (isConfirm) return;
        handleCancel();
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420,
          maxWidth: '95vw',
          maxHeight: '90vh',
          overflowY: 'auto',
          background: '#ffffff',
          borderRadius: 16,
          boxShadow: '0 20px 45px rgba(15,23,42,0.35)',
          padding: 16,
          border: `1px solid ${borderColor}33`,
        }}
      >
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div
            style={{
              width: 32,
              height: 32,
              borderRadius: 999,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: `${borderColor}11`,
              fontSize: 18,
            }}
          >
            {icon}
          </div>
          <div style={{ flex: 1 }}>
            <h3
              style={{
                margin: 0,
                marginBottom: 4,
                fontSize: 16,
                fontWeight: 600,
                color: '#111827',
              }}
            >
              {alert.title || (isConfirm ? 'Are you sure?' : 'Notice')}
            </h3>

            {alert.message && (
              <p
                style={{
                  margin: 0,
                  marginBottom: 10,
                  fontSize: 13,
                  color: '#4b5563',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {alert.message}
              </p>
            )}

            {alert.detail && (
              <p
                style={{
                  margin: 0,
                  marginBottom: 10,
                  fontSize: 12,
                  color: '#6b7280',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {alert.detail}
              </p>
            )}

            <div
              style={{
                marginTop: 8,
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 8,
              }}
            >
              {isConfirm ? (
                <>
                  <Button
                    onClick={handleCancel}
                    style={{ background: 'white', fontSize: 13 }}
                  >
                    {alert.cancelLabel || 'Cancel'}
                  </Button>
                  <DangerButton
                    onClick={handleConfirm}
                    style={{ fontSize: 13 }}
                  >
                    {alert.confirmLabel || 'Confirm'}
                  </DangerButton>
                </>
              ) : (
                <Button
                  onClick={handleCancel}
                  style={{ background: 'white', fontSize: 13 }}
                >
                  OK
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
